import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  CreateProductAislesPreferenceMutation,
  ProductAislesPreferenceCreateFailure,
  ProductAislesPreferenceCreateInput,
  ProductAislesPreferenceMutationCreateArgs,
} from "@/graphql/codegen/graphql"
import { CreateProductAislesPreferenceDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCreateProductAislesPreferenceReturn {
  create: (input: ProductAislesPreferenceCreateInput) => Promise<void>
  isCreating: boolean
  error?: CombinedError
}

export const useCreateProductAislesPreference = (onSuccess: () => void): UseCreateProductAislesPreferenceReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    CreateProductAislesPreferenceMutation,
    ProductAislesPreferenceMutationCreateArgs
  >(CreateProductAislesPreferenceDocument)

  const create = async (input: ProductAislesPreferenceCreateInput) => {
    const { data, error } = await executeMutation({ input })
    const output = data?.productAislesPreference.create
    const hasError = error || output?.__typename === "ProductAislesPreferenceCreateFailure"

    if (hasError) {
      notify({
        type: NotificationType.Error,
        message:
          error?.message ??
          (output as ProductAislesPreferenceCreateFailure).error.message ??
          "Failed to create product aisles preference.",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      content: "Product aisles preference successfully created.",
    })
    onSuccess()
  }

  return {
    create,
    isCreating: fetching,
    error,
  }
}
